import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory, useParams } from 'react-router-dom'
import ModalsComponent from '../../../components/stories/atoms/modals'
import { modalSizeOptions } from '../../../components/stories/atoms/modals/constants'
import TextInputComponent from '../../../components/stories/molecules/text-input'
import RadioComponent from '../../../components/stories/atoms/radio'
import {
  buttonVariantOptions,
  buttonSizeOptions
} from '../../../components/stories/atoms/button/constants'

import {
  FormContainer,
  MembershipContainer,
  ModalBody,
  ModalContentContainer,
  SectionTitle
} from '../styled-component'

import {
  textStyleWeightOptions,
  textStyleWeightStyleOptions
} from '../../../components/stories/fundamentals/text-styles/constants'
import {
  projectMenuApiErrorCodeOptions,
  projectMenuErrorMessages,
  membershipOptions,
  userRoleOptions
} from '../constants'
import { useCreateProjectMutation } from '../../../react-query/project/mutation/use-create-project-mutation'
import { useSetMemberRoleInProjectMutation } from '../../../react-query/project/mutation/use-set-member-role-in-project-mutation'
import { getUserId } from '../../../react-query/authentication/query/use-me-query'
import { useSnackbar } from '../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../components/stories/molecules/snackbar/constants'
import { textInputHintVariantOptions } from '../../../components/stories/molecules/text-input/constants'
import StaticBannerComponent from '../../../components/stories/molecules/static-banner'
import { staticBannerVariantOptions } from '../../../components/stories/molecules/static-banner/constants'
import RecursiveFolderList from '../../../components/recursive-folder-list'
import { useProjectMenuListViewControl } from '../../../hooks/use-project-menu-list-view-control'

const CreateProjectModal = () => {
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { addSnackbar } = useSnackbar()

  const { viewType, tab } = useProjectMenuListViewControl()
  const userId = getUserId()

  const { parentId: defaultFolderId } = useParams()

  const [projectName, setProjectName] = useState(null)
  const [projecNameHint, setProjectNameHint] = useState(null)
  const [parentFolderId, setParentFolderId] = useState(defaultFolderId ?? null)
  const [isMembership, setIsMemberShip] = useState(true)
  const [projectCreationError, setProjectCreationError] = useState('')

  const hasMiminumumProjectNameLength = projectName?.length > 3

  const MAX_CHARACTERS = 64
  const START_SHOW_COUNTER = MAX_CHARACTERS * 0.8
  const showCounter = projectName?.length >= START_SHOW_COUNTER

  const showSnackbarSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'projectMenu_project_created' }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  const handleOnClose = () => {
    history.push(`/project?view=${viewType}&tab=${tab}`)
  }

  const handleProjectCreationError = (error) => {
    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.NO_PERMISSION_TO_CREATE_PROJECT_OR_FOLDER
    ) {
      return setProjectCreationError(
        formatMessage({
          id: 'projectMenu_no_permission_to_create'
        })
      )
    }

    if (
      error &&
      error.message === projectMenuErrorMessages.PROJECT_NAME_IS_NOT_AVAILABLE
    ) {
      return setProjectNameHint(
        formatMessage({
          id: 'projectMenu_project_with_this_name_aready_exists'
        })
      )
    }

    if (
      error &&
      error?.response?.data?.status ===
        projectMenuApiErrorCodeOptions.FOLDER_NO_LOGER_EXISTS
    ) {
      return setProjectNameHint(
        formatMessage({
          id: 'recursiveFolderList_the_selected_folder_no_longer_exists'
        })
      )
    }

    return setProjectCreationError(
      formatMessage({
        id: 'projectMenu_project_create_error'
      })
    )
  }

  const handleProjectCreated = (_, mutationParams) => {
    const { projectId } = mutationParams

    handleOnClose()
    showSnackbarSuccess()
    history.push(`/project/${projectId}`)
  }

  const handleSelectFolderId = (folder) => {
    if (folder && folder.id) {
      return setParentFolderId(folder.id)
    }

    return setParentFolderId(null)
  }

  // Mutations
  const { mutate: createProject, isPending: createProjectLoading } =
    useCreateProjectMutation({
      onSuccess: handleSetUserRoleInProject,
      onError: handleProjectCreationError
    })

  const {
    mutate: setMemberRoleInProject,
    isPending: setMemberRoleInProjectLoading
  } = useSetMemberRoleInProjectMutation({
    onSuccess: handleProjectCreated,
    onError: handleProjectCreationError
  })

  // handlers
  function handleSetUserRoleInProject(projectId) {
    if (isMembership) {
      return setMemberRoleInProject({
        usersRoleList: [
          { userIds: [userId], role: userRoleOptions.POWER_USER }
        ],
        projectId
      })
    }
    handleProjectCreated()
  }

  const handleCheckNameMinimumLength = (e) => {
    const value = e.target.value
    if (value && value?.length > 0 && value?.length <= 3) {
      setProjectNameHint(
        formatMessage(
          { id: 'passwordOrientation_minimumLength' },
          { number: 4 }
        )
      )
    } else {
      setProjectNameHint(null)
    }
  }

  const handleChangeTitle = (e) => {
    handleCheckNameMinimumLength(e)
    setProjectName(e.target.value)
  }

  const handleSelectIsMembership = (value) => {
    if (value === membershipOptions.JOIN_AS_MEMBERSHIP) {
      return setIsMemberShip(true)
    }
    return setIsMemberShip(false)
  }

  const handleCreateProject = () => {
    setProjectNameHint(null)
    createProject({
      projectName,
      parentFolderId
    })
  }

  const isLoading = createProjectLoading || setMemberRoleInProjectLoading

  const modalButtons = [
    {
      title: formatMessage({ id: 'cancel' }),
      variant: buttonVariantOptions['TERTIARY-HOLLOW'],
      size: buttonSizeOptions.MEDIUM,
      onClick: handleOnClose
    },
    {
      title: formatMessage({ id: 'create' }),
      variant: buttonVariantOptions.PRIMARY,
      size: buttonSizeOptions.MEDIUM,
      onClick: handleCreateProject,
      disabled: isLoading || !hasMiminumumProjectNameLength,
      isLoading
    }
  ]

  return (
    <ModalsComponent
      isOpen={true}
      title={formatMessage({ id: 'projects_createProject' })}
      size={modalSizeOptions.SMALL}
      rightButtons={modalButtons}
      autoHeight
      showCloseButton={false}
      onClose={handleOnClose}
    >
      <ModalBody>
        {projectCreationError && (
          <StaticBannerComponent
            variant={staticBannerVariantOptions.ERROR}
            message={projectCreationError}
          />
        )}
        <ModalContentContainer>
          <SectionTitle>
            <FormattedMessage id="projectSettings_details" />
          </SectionTitle>

          <FormContainer>
            <TextInputComponent
              defaultValue={projectName}
              label={formatMessage({ id: 'title' })}
              maxCharacters={64}
              showCounter={showCounter}
              onChange={handleChangeTitle}
              hintShow={projecNameHint}
              hintMessage={projecNameHint}
              hintVariant={textInputHintVariantOptions.ERROR}
              isTextarea
              focusOnMount
            />

            <RecursiveFolderList
              onSelectItem={handleSelectFolderId}
              defaultFolderId={defaultFolderId}
            />

            <MembershipContainer>
              <SectionTitle>
                <FormattedMessage id="projectMenu_your_membership" />
              </SectionTitle>

              <RadioComponent.Root
                defaultValue={membershipOptions.JOIN_AS_MEMBERSHIP}
                loading={false}
                onValueChange={handleSelectIsMembership}
              >
                <RadioComponent.Item
                  value={membershipOptions.JOIN_AS_MEMBERSHIP}
                  label={formatMessage({
                    id: 'projectMenu_join_as_member'
                  })}
                  labelCustomStyle={{
                    ...textStyleWeightStyleOptions[
                      textStyleWeightOptions['TEXT-600']
                    ]
                  }}
                  secondaryLabel={formatMessage({
                    id: 'projectMenu_join_as_member_description'
                  })}
                  labelContainerCustomStyle={{ paddingLeft: '0.75rem' }}
                >
                  <RadioComponent.Indicator />
                </RadioComponent.Item>

                <RadioComponent.Item
                  value={membershipOptions.DO_NOT_JOIN_AS_MEMBER}
                  label={formatMessage({ id: 'projectMenu_do_not_join' })}
                  labelCustomStyle={{
                    ...textStyleWeightStyleOptions[
                      textStyleWeightOptions['TEXT-600']
                    ]
                  }}
                  secondaryLabel={formatMessage({
                    id: 'projectMenu_do_not_join_description'
                  })}
                  labelContainerCustomStyle={{ paddingLeft: '0.75rem' }}
                >
                  <RadioComponent.Indicator />
                </RadioComponent.Item>
              </RadioComponent.Root>
            </MembershipContainer>
          </FormContainer>
        </ModalContentContainer>
      </ModalBody>
    </ModalsComponent>
  )
}

export default CreateProjectModal
